import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { localizedPath } from '../seo/routes';
import {
  CheckCircleIcon,
  SparklesIcon,
  ClockIcon,
  HandRaisedIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
  PhoneIcon
} from '@heroicons/react/24/solid';
import { useLanguage } from '../contexts/LanguageContext';

type IndicationItem = { title: string; body: string };
type ProcessItem = { title: string; body: string };
type BenefitItem = { label: string; value: string };
type FaqItem = { q: string; a: string };

const CompositePage: React.FC = () => {
  const { translations, language } = useLanguage();
  const t = translations.composite;
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const bookingPath = `${localizedPath('contact', language)}?booking=true&treatment=composite`;
  const whyIcons = [ClockIcon, HandRaisedIcon, ShieldCheckIcon];

  return (
    <main className="pt-20 lg:pt-32 bg-white">
      <Seo pageKey="composite" />
      {/* Hero */}
      <section className="bg-navy text-white py-20">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="text-mint font-semibold tracking-wider uppercase text-sm mb-4">
              {translations.nav.composite}
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {t.title}
            </h1>
            <p className="text-xl text-white/90 mb-10">{t.intro}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={bookingPath} className="btn btn-primary">
                {t.ctaButton}
              </Link>
              <a href="tel:+34603319648" className="btn bg-white/10 text-white hover:bg-white hover:text-navy flex items-center justify-center">
                <PhoneIcon className="w-4 h-4 mr-2" />
                +34 603 31 96 48
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Image: Vorher / Nachher */}
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/seadent-composite-before-after.webp"
                alt={t.heroAlt}
                className="w-full h-auto block"
                width={1600}
                height={1281}
                decoding="async"
                loading="lazy"
              />
            </div>
            <p className="text-center text-gray-600 italic mt-4 max-w-3xl mx-auto">
              {t.heroCaption}
            </p>
          </div>
        </div>
      </section>

      {/* Was ist? */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-navy mb-6">{t.whatIsTitle}</h2>
            <p className="text-lg text-gray-800 mb-4">{t.whatIsBody}</p>
            <p className="text-xl text-navy font-semibold">{t.whatIsResult}</p>
          </div>

          {/* Abgrenzung zu Veneers */}
          <div className="max-w-3xl mx-auto mt-10 bg-mint/10 border-l-4 border-mint rounded-r-xl p-6 md:p-8">
            <div className="flex items-start">
              <SparklesIcon className="w-6 h-6 text-mint mt-1 mr-4 flex-shrink-0" />
              <div>
                <h3 className="text-navy font-bold text-xl mb-2">{t.distinctionTitle}</h3>
                <p className="text-gray-800">{t.distinctionBody}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Indikationen */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <h2 className="text-navy text-center mb-12">{t.indicationsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {(t.indications as IndicationItem[]).map((ind, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-soft hover:shadow-premium transition-shadow">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-mint mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-navy font-bold text-lg mb-2">{ind.title}</h3>
                    <p className="text-gray-700">{ind.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Was unsere Methode besonders macht */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 lg:order-1">
              <h2 className="text-navy mb-8">{t.whyTitle}</h2>
              <div className="space-y-6">
                {(t.why as IndicationItem[]).map((item, i) => {
                  const Icon = whyIcons[i] || SparklesIcon;
                  return (
                    <div key={i} className="flex items-start">
                      <div className="bg-mint/15 rounded-full p-3 mr-4 flex-shrink-0">
                        <Icon className="w-6 h-6 text-mint" />
                      </div>
                      <div>
                        <h3 className="text-navy font-bold text-lg mb-1">{item.title}</h3>
                        <p className="text-gray-700">{item.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <img
                src="/images/seadent-composite-buildup.webp"
                alt={t.whyBuildupAlt}
                className="rounded-2xl shadow-2xl w-full"
                width={1600}
                height={1600}
                decoding="async"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Behandlungsablauf */}
      <section className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="container-custom">
          <h2 className="text-navy text-center mb-12">{t.processTitle}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <ol className="space-y-5">
                {(t.process as ProcessItem[]).map((step, i) => (
                  <li key={i} className="flex items-start">
                    <span className="bg-navy text-white rounded-full w-9 h-9 flex items-center justify-center font-bold text-sm mr-4 flex-shrink-0">
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="text-navy font-bold text-lg mb-1">{step.title}</h3>
                      <p className="text-gray-700">{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <img
                src="/images/seadent-composite-illustration.webp"
                alt={t.processIllustrationAlt}
                className="rounded-2xl shadow-xl w-full bg-white"
                width={1600}
                height={1344}
                decoding="async"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Nach der Behandlung + Haltbarkeit */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div>
              <h2 className="text-navy mb-6">{t.aftercareTitle}</h2>
              <ul className="space-y-3">
                {(t.aftercare as string[]).map((item, i) => (
                  <li key={i} className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-mint mt-1 mr-3 flex-shrink-0" />
                    <span className="text-gray-800">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-navy mb-6">{t.longevityTitle}</h2>
              <p className="text-lg text-gray-800">{t.longevityBody}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vorteile auf einen Blick */}
      <section className="py-20 bg-navy text-white">
        <div className="container-custom">
          <h2 className="text-center mb-12 text-white">{t.benefitsTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {(t.benefits as BenefitItem[]).map((b, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <div className="text-mint font-bold text-sm uppercase tracking-wider mb-2">
                  {b.label}
                </div>
                <div className="text-white/90 text-lg">{b.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <h2 className="text-navy text-center mb-12">{t.faqTitle}</h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {(t.faq as FaqItem[]).map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-gray-100 transition-colors"
                    aria-expanded={isOpen}
                  >
                    <span className="text-navy font-semibold text-lg pr-4">{item.q}</span>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-mint flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 text-gray-700 leading-relaxed">{item.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-mint/10 via-white to-blue-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-navy mb-6">{t.ctaTitle}</h2>
            <p className="text-lg text-gray-700 mb-10">{t.ctaBody}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to={bookingPath} className="btn btn-primary shadow-premium">
                {t.ctaButton}
              </Link>
              <a href="tel:+34603319648" className="btn bg-light-blue text-navy hover:bg-navy hover:text-white flex items-center justify-center">
                <PhoneIcon className="w-4 h-4 mr-2" />
                {t.ctaCall}: +34 603 31 96 48
              </a>
            </div>
            <p className="mt-6 text-sm text-gray-500">
              Avenida de Jaime III № 3, 07012 Palma de Mallorca
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CompositePage;
